import React, { useState } from "react";

import { Flex, Icon, Popover, Spin, Tooltip, Typography } from "components";

export type HeaderItemSwitcherItem = {
  id: string;
  label: string;
  subLabel?: string;
  actionIcon?: React.ReactNode;
};

export type HeaderItemSwitcherProps = {
  items: HeaderItemSwitcherItem[];
  activeItemId?: string;
  isLoading?: boolean;
  onSelect: (itemId: string) => void;
  footerLabel?: string;
  onFooterClick?: () => void;
  dataTestId?: string;
  truncateTriggerLabelAt?: number;
};

const ITEM_LABEL_VISIBLE_CHARACTERS = 24;

const truncateLabel = (label: string, maxLength?: number) => (maxLength && label.length > maxLength ? `${label.slice(0, maxLength)}...` : label);

export const HeaderItemSwitcher = ({
  items,
  activeItemId,
  isLoading,
  onSelect,
  footerLabel,
  onFooterClick,
  dataTestId = "header-item-switcher",
  truncateTriggerLabelAt,
}: HeaderItemSwitcherProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const activeItem = items.find((item) => item.id === activeItemId);

  const handleSelect = (itemId: string) => {
    if (!itemId) return;

    setIsOpen(false);
    onSelect(itemId);
  };

  const itemsList = (
    <Flex className="header-item-switcher navigation-list" vertical>
      <Flex className="header-item-switcher navigation-list container" vertical>
        {items.map((item, i) => (
          <Flex
            key={item.id}
            className="header-item-switcher navigation-list item"
            data-testid={`${dataTestId}-item-${i}`}
            align="center"
            justify="space-between"
            gap="0.5rem"
            onClick={() => handleSelect(item.id)}
          >
            <Flex align="center" gap="0.5rem" flex={1} style={{ minWidth: 0 }}>
              <span style={{ minWidth: "1.5rem", flexShrink: 0 }}>
                {activeItemId === item.id ? (
                  <span data-testid={`${dataTestId}-item-active-check`}>
                    <Icon iconName="tabler/circle-check" color="black" />
                  </span>
                ) : null}
              </span>
              <Flex vertical style={{ minWidth: 0 }}>
                {/* drawn inside the dropdown, not Ant's body portal, so it is removed along with it */}
                <Tooltip
                  variant="compact"
                  title={item.label.length > ITEM_LABEL_VISIBLE_CHARACTERS ? item.label : undefined}
                  getPopupContainer={(triggerNode) => (triggerNode.closest(".ant-popover") as HTMLElement) ?? document.body}
                >
                  <Typography variant="paragraph-m" noWrap className="item-label">
                    {item.label}
                  </Typography>
                </Tooltip>
                {item.subLabel ? (
                  <Typography variant="paragraph-xs" className="item-sub-label">
                    {item.subLabel}
                  </Typography>
                ) : null}
              </Flex>
            </Flex>
            {item.actionIcon ? (
              <Flex className="item-action" onClickCapture={() => setIsOpen(false)}>
                {item.actionIcon}
              </Flex>
            ) : null}
          </Flex>
        ))}
      </Flex>
      {onFooterClick ? (
        <Flex
          className="new-item-button"
          data-testid={`${dataTestId}-footer-button`}
          align="center"
          justify="flex-start"
          gap="1rem"
          onClick={() => {
            setIsOpen(false);
            onFooterClick();
          }}
        >
          <Icon className="new-item-button-icon" iconName="klim/plus" />
          <Typography variant="paragraph-m" color="green-400">
            {footerLabel}
          </Typography>
        </Flex>
      ) : null}
    </Flex>
  );

  return (
    <Flex className="header-item-switcher-wrapper" align="center" justify="flex-start" gap="0.5rem" flex={1}>
      <Popover
        dataTestId={`${dataTestId}-popover`}
        className="header-item-switcher"
        placement="bottomLeft"
        arrow={false}
        content={itemsList}
        trigger="click"
        open={isOpen}
        onOpenChange={() => setIsOpen(!isOpen)}
      >
        {isLoading ? (
          <Spin size="default" />
        ) : items.length < 1 || !activeItemId ? null : (
          <Flex
            data-testid={dataTestId}
            className="header-item-switcher button pointer"
            justify="center"
            align="center"
            onClick={() => setIsOpen(!isOpen)}
            gap="0.5rem"
          >
            <Typography variant="paragraph-m">{activeItem ? truncateLabel(activeItem.label, truncateTriggerLabelAt) : null}</Typography>
            <Icon className={`header-item-switcher collapse-chevron ${!isOpen && "reverse"}`} iconName="tabler/chevron-up" size="s" />
          </Flex>
        )}
      </Popover>
    </Flex>
  );
};
